import { useMemo } from 'react';
import { useSortBy } from './useSortBy'; // Import your existing hook

type SortHandler<T> = (a: T, b: T) => number;

type SortOptionConfig<T> = {
    label: string;
    Icon: React.ReactElement;
    handler: SortHandler<T>;
};

type SortOptionsMap<T> = Record<string, SortOptionConfig<T>>;

// Main hook
export function useSortByWithConfig<
    T,
    const OptionsMap extends SortOptionsMap<T>
>(
    options: OptionsMap,
    data: T[],
    initialOption?: keyof OptionsMap
) {
    // Validate inputs
    if (!options || Object.keys(options).length === 0) {
        throw new Error('useSortByWithConfig: options cannot be empty');
    }

    if (!Array.isArray(data)) {
        console.warn('useSortByWithConfig: data should be an array');
    }

    // Extract option keys for the base useSortBy hook
    const sortKeys = Object.keys(options) as (keyof OptionsMap)[];

    // Use the base useSortBy hook
    const sortByHook = useSortBy(sortKeys as string[], initialOption as string);

    const { currentOption } = sortByHook;

    // Get current option configuration
    const currentConfig = options[currentOption];

    // Memoized sorted result
    const sortedResult = useMemo(() => {
        if (!data || data.length === 0) return [];

        const handler = currentConfig?.handler;
        if (!handler || typeof handler !== 'function') {
            console.warn(`useSortByWithConfig: handler for "${String(currentOption)}" is not a function`);
            return [...data];
        }

        return [...data].sort(handler);
    }, [data, currentConfig?.handler, currentOption]);

    // Helper functions
    const getSortConfig = (option: keyof OptionsMap) => {
        return options[option];
    };

    const getAvailableOptions = () => {
        return sortKeys.map(key => ({
            key,
            ...options[key]
        }));
    };

    const sortByLabel = (label: string) => {
        const option = sortKeys.find(key => options[key].label === label);
        if (option) {
            sortByHook.sortByOption(option as string);
        } else {
            console.warn(`useSortByWithConfig: no option found with label "${label}"`);
        }
    };

    return {
        // From base useSortBy hook
        ...sortByHook,

        // New additions
        sortedResult,
        label: currentConfig?.label || '',
        Icon: currentConfig?.Icon || null,
        currentConfig,

        // Utility functions
        getSortConfig,
        getAvailableOptions,
        sortByLabel,

        // Metadata
        totalItems: data?.length || 0,
        hasResults: sortedResult.length > 0,
    } as const;
}